/** Shared Product Alert composite subscription helpers (UI layer). */

export const ALERT_OPENED_KEY = "product.alert_opened";
export const ALERT_CLOSED_KEY = "product.alert_closed";
export const ALERT_EXTENDED_KEY = "product.alert_extended";

/** Synthetic picker key — not a real system event. */
export const COMPOSITE_ALERT_PICKER_KEY = "product.alert";

export const COMPOSITE_ALERT_TITLE = "Product Alert";
export const COMPOSITE_ALERT_DESCRIPTION = "A product sends an alert";
export const COMPOSITE_ALERT_SUMMARY_TEMPLATE =
  "Notify {subscriberLabel} whenever {entityLabel} sends an alert";

export const DEFAULT_ALERT_ENABLED_SEVERITIES = [3, 4, 5];

export const ALERT_SEVERITY_LEVELS: {
  level: number;
  label: string;
  /** Lucide icon name key used by severity toggles. */
  icon: "lightbulb" | "info" | "circleAlert" | "triangleAlert" | "octagonAlert";
}[] = [
  { level: 1, label: "Suggestion", icon: "lightbulb" },
  { level: 2, label: "Low", icon: "info" },
  { level: 3, label: "Medium", icon: "circleAlert" },
  { level: 4, label: "High", icon: "triangleAlert" },
  { level: 5, label: "Critical", icon: "octagonAlert" },
];

export function isAlertEventKey(key: string): boolean {
  return key === ALERT_OPENED_KEY || key === ALERT_CLOSED_KEY;
}

export function normalizeAlertCode(code: string): string {
  return String(code).trim().toUpperCase();
}

export type AlertOverride = { code: string; mode: "allow" | "block" };

export type AlertPrefsDraft = {
  enabledSeverities: number[];
  overrides: AlertOverride[];
};

export function cloneAlertPrefs(prefs: AlertPrefsDraft): AlertPrefsDraft {
  return {
    enabledSeverities: [...prefs.enabledSeverities].sort((a, b) => a - b),
    overrides: prefs.overrides.map((o) => ({
      code: normalizeAlertCode(o.code),
      mode: o.mode,
    })),
  };
}

export function alertPrefsEqual(a: AlertPrefsDraft, b: AlertPrefsDraft): boolean {
  const ae = [...a.enabledSeverities].sort((x, y) => x - y).join(",");
  const be = [...b.enabledSeverities].sort((x, y) => x - y).join(",");
  if (ae !== be) return false;
  return alertOverridesEqual(a, b);
}

export function alertOverridesEqual(a: AlertPrefsDraft, b: AlertPrefsDraft): boolean {
  const ao = [...a.overrides]
    .map((o) => `${normalizeAlertCode(o.code)}:${o.mode}`)
    .sort()
    .join("|");
  const bo = [...b.overrides]
    .map((o) => `${normalizeAlertCode(o.code)}:${o.mode}`)
    .sort()
    .join("|");
  return ao === bo;
}

/** Count discrete preference diffs (severities + per-code overrides). */
export function countAlertPrefsChanges(
  current: AlertPrefsDraft,
  baseline: AlertPrefsDraft,
): number {
  let count = 0;
  for (const { level } of ALERT_SEVERITY_LEVELS) {
    const a = current.enabledSeverities.includes(level);
    const b = baseline.enabledSeverities.includes(level);
    if (a !== b) count += 1;
  }
  const currentByCode = new Map(
    current.overrides.map((o) => [normalizeAlertCode(o.code), o.mode] as const),
  );
  const baselineByCode = new Map(
    baseline.overrides.map((o) => [normalizeAlertCode(o.code), o.mode] as const),
  );
  const codes = new Set([...currentByCode.keys(), ...baselineByCode.keys()]);
  for (const code of codes) {
    if (currentByCode.get(code) !== baselineByCode.get(code)) count += 1;
  }
  return count;
}

/** True exception relative to current severity selection (for pin/badge). */
export function isActiveException(
  prefs: AlertPrefsDraft,
  code: string,
  catalogSeverity: number,
): boolean {
  const enabled = new Set(prefs.enabledSeverities);
  const override = prefs.overrides.find(
    (o) => normalizeAlertCode(o.code) === normalizeAlertCode(code),
  );
  if (!override) return false;
  const included = enabled.has(catalogSeverity);
  if (override.mode === "allow") return !included;
  if (override.mode === "block") return included;
  return false;
}

export function listActiveExceptions(
  prefs: AlertPrefsDraft,
  alerts: { code: string; severity: number; name: string }[],
): { code: string; mode: "allow" | "block"; name: string; severity: number }[] {
  const byCode = new Map(
    alerts.map((a) => [normalizeAlertCode(a.code), a]),
  );
  const out: {
    code: string;
    mode: "allow" | "block";
    name: string;
    severity: number;
  }[] = [];
  for (const o of prefs.overrides) {
    const def = byCode.get(normalizeAlertCode(o.code));
    const severity = def?.severity ?? 3;
    if (!isActiveException(prefs, o.code, severity)) continue;
    out.push({
      code: normalizeAlertCode(o.code),
      mode: o.mode,
      name: def?.name ?? o.code,
      severity,
    });
  }
  return out.sort((a, b) => a.code.localeCompare(b.code));
}

/**
 * Derive membership for Allowed / Blocked lists.
 * Base: severity in enabled set → allowed, else blocked.
 * Overrides flip membership; redundant overrides still kept in prefs storage.
 */
export function partitionAlerts(
  prefs: AlertPrefsDraft,
  alerts: { code: string; name: string; type: string; severity: number }[],
): {
  allowed: { code: string; name: string; type: string; severity: number; isException: boolean }[];
  blocked: { code: string; name: string; type: string; severity: number; isException: boolean }[];
} {
  const enabled = new Set(prefs.enabledSeverities);
  const overrideByCode = new Map(
    prefs.overrides.map((o) => [normalizeAlertCode(o.code), o.mode]),
  );

  const allowed: {
    code: string;
    name: string;
    type: string;
    severity: number;
    isException: boolean;
  }[] = [];
  const blocked: typeof allowed = [];

  for (const alert of alerts) {
    const code = normalizeAlertCode(alert.code);
    const mode = overrideByCode.get(code);
    const severityIncluded = enabled.has(alert.severity);
    let isAllowed = severityIncluded;
    if (mode === "allow") isAllowed = true;
    if (mode === "block") isAllowed = false;
    const isException = isActiveException(prefs, code, alert.severity);
    const row = {
      code,
      name: alert.name,
      type: alert.type,
      severity: alert.severity,
      isException,
    };
    if (isAllowed) allowed.push(row);
    else blocked.push(row);
  }

  const sortFn = (
    a: (typeof allowed)[number],
    b: (typeof allowed)[number],
  ) => {
    if (a.isException !== b.isException) return a.isException ? -1 : 1;
    return a.code.localeCompare(b.code);
  };
  allowed.sort(sortFn);
  blocked.sort(sortFn);
  return { allowed, blocked };
}

export function setOverride(
  prefs: AlertPrefsDraft,
  code: string,
  mode: "allow" | "block" | null,
): AlertPrefsDraft {
  const normalized = normalizeAlertCode(code);
  const overrides = prefs.overrides.filter(
    (o) => normalizeAlertCode(o.code) !== normalized,
  );
  if (mode) overrides.push({ code: normalized, mode });
  return { ...prefs, overrides };
}

/**
 * Whether notifications are enabled for a specific alert code given prefs.
 * Does not check whether the Product Alert event subscription itself exists.
 */
export function isAlertNotifyEnabled(
  prefs: AlertPrefsDraft,
  code: string,
  severity: number,
): boolean {
  const enabled = new Set(prefs.enabledSeverities);
  const normalized = normalizeAlertCode(code);
  const override = prefs.overrides.find(
    (o) => normalizeAlertCode(o.code) === normalized,
  );
  let isAllowed = enabled.has(severity);
  if (override?.mode === "allow") isAllowed = true;
  if (override?.mode === "block") isAllowed = false;
  return isAllowed;
}

/**
 * Move an alert into the Allowed or Blocked list via an override.
 * Overrides are always stored so they persist if severity selection later changes.
 */
export function moveAlertToList(
  prefs: AlertPrefsDraft,
  code: string,
  _catalogSeverity: number,
  target: "allowed" | "blocked",
): AlertPrefsDraft {
  return setOverride(prefs, code, target === "allowed" ? "allow" : "block");
}
